import React from 'react';
import { get } from 'lodash';
import { withRouter } from 'react-router-dom';
import searchQuery from 'query-string';

// React view component to render
import { memberClaimDetailSummary as MemberClaimDetailSummaryView } from 'ui/views/widgets/memberClaimDetailSummary';
import { MemberClaimDetailSummaryService } from 'services/memberClaimDetailSummary';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import * as errorOverlay from 'utils/errorOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { OVERLAY_VIEWS } from 'constants/templates';
import { overlay } from 'modules/overlay';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberClaimDetailSummary } from 'ui/containers/MemberClaimDetailSummary';
 *
 * // render
 * <MemberClaimDetailSummary propName='propValue'/>
 */
export class _MemberClaimDetailSummary extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.handleActionClick = this.handleActionClick.bind( this );
        this.createClaimToDo = this.createClaimToDo.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
        };

        // create service instance
        this.memberClaimDetailSummaryService = new MemberClaimDetailSummaryService();

        // start data fetch
        this.fetchComponentData();
         
    }

    // fetch component Data
    async fetchComponentData() {
        log.debug( 'MemberClaimDetailSummary.fetchComponentData()' );

        // cancel ongoing request
        this.memberClaimDetailSummaryService.cancel();

        // get status and service data
        const { status, data } = await this.memberClaimDetailSummaryService.fetchComponentData( {
            url: this.props.widget.url,
            data: {
                ...this.props.requestPayload
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }

    async createClaimToDo( { action, payload } ) {
        log.debug( 'MemberClaimDetailSummary.createClaimToDo()' );

        pageLevelPreloader.show();

        // cancel ongoing request
        this.memberClaimDetailSummaryService.cancel();

        // get status and service data
        const { status, data } = await this.memberClaimDetailSummaryService.fetchClaimToDoForm( {
            url: action.url,
            data: {
                ...this.props.requestPayload
            }
        } );

        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            
            errorOverlay.open();

        } else {

            // open overlay and send the `data`
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.CLAIM_TODO,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...payload,
                    ...action,
                    ...data,
                    onCancelClick: () => {
                        overlay.close();
                    },
                }
            } );
        }

    }

    handleActionClick( { type, value, item } ) {
        log.debug( 'MemberClaimDetailSummary.handleActionClick()' );

        // check if URL container `memberId` query parameter.
        const queryParams = searchQuery.parse( this.props.location.search );
        const memberId = get( this.props.location, 'state.memberId', undefined );
        const patientId = get( this.props.location, 'state.patientId', undefined );
        if( 'cell' === type && 'rxNumber' === item.name ) {
            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.PRESCRIPTION_DETAIL,
                search: searchQuery.stringify( {
                    rxId: item.value,
                } ),
                state: {  memberId: memberId, patientId: patientId }
            } );
        }

        if( 'headerAction' === type && 'creareClaimtToDo' ) {
            this.createClaimToDo( {
                payload: {
                    context: {
                        memberId,
                    claimId: get( queryParams, 'claimId' )
                    }
                },
                action: value
            } );
        }
    }

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.memberClaimDetailSummaryService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberClaimDetailSummaryView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                componentData={ get( this.state, 'componentData', {} ) }
                onActionClick={ this.handleActionClick }
            />
        );
    }
}

// set display name
_MemberClaimDetailSummary.displayName = '_MemberClaimDetailSummary';

// set default props
_MemberClaimDetailSummary.defaultProps = {};

// prop types
_MemberClaimDetailSummary.propTypes = {};

// with router functionality
export const MemberClaimDetailSummary = withRouter( _MemberClaimDetailSummary );
